"use client"

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

interface FormData {
  brand: string
  model: string
  modelNumber: string
  hasBox: string
  hasPapers: string
  photos: string[]
  name: string
  email: string
  phone: string
  website?: string
}

interface UploadedFile {
  url: string
  name: string
}

interface WatchFormContextType {
  formData: FormData
  uploadedFiles: UploadedFile[]
  currentStep: number
  updateFormData: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  setUploadedFiles: (files: UploadedFile[]) => void
  setCurrentStep: (step: number) => void
  clearForm: () => void
  hasRestoredData: boolean
}

const initialFormData: FormData = {
  brand: "",
  model: "",
  modelNumber: "",
  hasBox: "",
  hasPapers: "",
  photos: [],
  name: "",
  email: "",
  phone: "",
  website: ""
}

const WatchFormContext = createContext<WatchFormContextType | undefined>(undefined)

const STORAGE_KEY = 'watchFormData'
const STORAGE_FILES_KEY = 'watchFormUploadedFiles'
const STORAGE_STEP_KEY = 'watchFormCurrentStep'
const STORAGE_TIMESTAMP_KEY = 'watchFormTimestamp'
const STORAGE_PHOTO_TIMESTAMP_KEY = 'watchFormPhotoTimestamp'

// TTL: 24 hours for form data (in milliseconds)
const FORM_DATA_TTL = 24 * 60 * 60 * 1000
// TTL: 1 hour for photos (save storage and cleanup abandoned uploads)
const PHOTO_TTL = 60 * 60 * 1000

export function WatchFormProvider({ children }: { children: React.ReactNode }) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [uploadedFiles, setUploadedFilesState] = useState<UploadedFile[]>([])
  const [currentStep, setCurrentStepState] = useState(1)
  const [hasRestoredData, setHasRestoredData] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  // Load data from localStorage on mount
  useEffect(() => {
    const cleanupAbandonedPhotos = async (photoUrls: string[]) => {
      if (photoUrls.length === 0) return
      
      console.log('Cleaning up abandoned photos:', photoUrls.length)
      try {
        for (const url of photoUrls) {
          await fetch("/api/uploadthing", {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url }),
          })
        }
      } catch (error) {
        console.error('Error cleaning up abandoned photos:', error)
      }
    }

    try {
      const savedTimestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY)
      const savedPhotoTimestamp = localStorage.getItem(STORAGE_PHOTO_TIMESTAMP_KEY)
      const now = Date.now()

      // Check if form data has expired
      if (savedTimestamp) {
        const timestamp = Number(savedTimestamp)
        const age = now - timestamp

        if (age > FORM_DATA_TTL) {
          // Data expired - clear everything
          console.log('Form data expired, clearing localStorage')
          
          // Try to cleanup any abandoned photos before clearing
          const savedFiles = localStorage.getItem(STORAGE_FILES_KEY)
          if (savedFiles) {
            try {
              const files = JSON.parse(savedFiles) as UploadedFile[]
              void cleanupAbandonedPhotos(files.map(f => f.url))
            } catch {}
          }
          
          localStorage.removeItem(STORAGE_KEY)
          localStorage.removeItem(STORAGE_FILES_KEY)
          localStorage.removeItem(STORAGE_STEP_KEY)
          localStorage.removeItem(STORAGE_TIMESTAMP_KEY)
          localStorage.removeItem(STORAGE_PHOTO_TIMESTAMP_KEY)
          setIsHydrated(true)
          return
        }
      }

      // Check if photos have expired (separate shorter TTL)
      const savedFiles = localStorage.getItem(STORAGE_FILES_KEY)
      let shouldClearPhotos = false

      if (savedPhotoTimestamp && savedFiles) {
        const photoTimestamp = Number(savedPhotoTimestamp)
        const photoAge = now - photoTimestamp

        if (photoAge > PHOTO_TTL) {
          console.log('Photo storage expired (1 hour), clearing photos from localStorage')
          shouldClearPhotos = true
          
          // Cleanup abandoned UploadThing files
          try {
            const files = JSON.parse(savedFiles) as UploadedFile[]
            void cleanupAbandonedPhotos(files.map(f => f.url))
          } catch {}
          
          localStorage.removeItem(STORAGE_FILES_KEY)
          localStorage.removeItem(STORAGE_PHOTO_TIMESTAMP_KEY)
        }
      }

      // Data is fresh or no timestamp exists - load it
      const savedData = localStorage.getItem(STORAGE_KEY)
      const savedStep = localStorage.getItem(STORAGE_STEP_KEY)

      if (savedData) {
        const parsed = JSON.parse(savedData)
        setFormData(parsed)
        setHasRestoredData(true)
      }

      if (!shouldClearPhotos && savedFiles) {
        const parsed = JSON.parse(savedFiles)
        setUploadedFilesState(parsed)
      }

      if (savedStep) {
        setCurrentStepState(Number(savedStep))
      }

      // Update timestamp on load (extends TTL on user return)
      if (savedData || savedFiles || savedStep) {
        localStorage.setItem(STORAGE_TIMESTAMP_KEY, now.toString())
        if (!shouldClearPhotos && savedFiles) {
          localStorage.setItem(STORAGE_PHOTO_TIMESTAMP_KEY, now.toString())
        }
      }
    } catch (error) {
      console.error('Failed to restore form data:', error)
    } finally {
      setIsHydrated(true)
    }
  }, [])

  // Save to localStorage whenever data changes
  useEffect(() => {
    if (!isHydrated) return // Don't save during initial hydration
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData))
      // Update timestamp on every save
      localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString())
    } catch (error) {
      console.error('Failed to save form data:', error)
    }
  }, [formData, isHydrated])

  useEffect(() => {
    if (!isHydrated) return
    
    try {
      localStorage.setItem(STORAGE_FILES_KEY, JSON.stringify(uploadedFiles))
      // Update photo timestamp whenever photos change
      localStorage.setItem(STORAGE_PHOTO_TIMESTAMP_KEY, Date.now().toString())
    } catch (error) {
      console.error('Failed to save uploaded files:', error)
    }
  }, [uploadedFiles, isHydrated])

  useEffect(() => {
    if (!isHydrated) return
    
    try {
      localStorage.setItem(STORAGE_STEP_KEY, currentStep.toString())
    } catch (error) {
      console.error('Failed to save current step:', error)
    }
  }, [currentStep, isHydrated])

  const updateFormData = useCallback(<K extends keyof FormData>(
    field: K,
    value: FormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }, [])

  const setUploadedFiles = useCallback((files: UploadedFile[]) => {
    setUploadedFilesState(files)
  }, [])

  const setCurrentStep = useCallback((step: number) => {
    setCurrentStepState(step)
  }, [])

  const clearForm = useCallback(() => {
    setFormData(initialFormData)
    setUploadedFilesState([])
    setCurrentStepState(1)
    setHasRestoredData(false)
    
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(STORAGE_FILES_KEY)
      localStorage.removeItem(STORAGE_STEP_KEY)
      localStorage.removeItem(STORAGE_TIMESTAMP_KEY)
      localStorage.removeItem(STORAGE_PHOTO_TIMESTAMP_KEY)
    } catch (error) {
      console.error('Failed to clear form data from localStorage:', error)
    }
  }, [])

  const value = {
    formData,
    uploadedFiles,
    currentStep,
    updateFormData,
    setUploadedFiles,
    setCurrentStep,
    clearForm,
    hasRestoredData
  }

  return (
    <WatchFormContext.Provider value={value}>
      {children}
    </WatchFormContext.Provider>
  )
}

export function useWatchForm() {
  const context = useContext(WatchFormContext)
  if (context === undefined) {
    throw new Error('useWatchForm must be used within a WatchFormProvider')
  }
  return context
}
